import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { BaseResponse, Result } from '@/lib/http/HttpClient';

/**
 * Options for the useApiCall hook
 */
interface UseApiCallOptions<T extends BaseResponse> {
  /**
   * Function that makes the API call
   */
  apiFn: (signal: AbortSignal) => Promise<Result<T>>;

  /**
   * Dependencies array - when these change, the API call is re-executed
   */
  deps?: React.DependencyList;

  /**
   * Whether to execute the API call immediately on mount
   * @default true
   */
  immediate?: boolean;

  /**
   * Custom error handler - called when an error occurs after token refresh completes
   */
  onError?: (error: string) => void;

  /**
   * Success handler - called when the API call succeeds
   */
  onSuccess?: (data: T) => void;
}

/**
 * Result returned by the useApiCall hook
 */
interface UseApiCallResult<T extends BaseResponse> {
  /**
   * The data returned from the API call (null if loading, error, or not yet called)
   */
  data: T | null;

  /**
   * Error message (null if no error or if token refresh is in progress)
   */
  error: string | null;

  /**
   * True if the API call is in progress OR if token refresh is in progress
   */
  isLoading: boolean;

  /**
   * Function to retry the API call manually
   */
  retry: () => void;

  /**
   * Function to execute the API call manually (for non-immediate calls)
   */
  execute: () => Promise<void>;
}

/**
 * Smart API call hook that handles token refresh transparently
 *
 * This hook wraps API calls and provides intelligent loading state management:
 * - Shows loading state during API call AND during token refresh
 * - Hides errors during token refresh (shows loading instead)
 * - Only shows errors after token refresh completes or if refresh fails
 * - Automatically retries on token refresh
 *
 * @example
 * ```tsx
 * const { data, error, isLoading, retry } = useApiCall({
 *   apiFn: (signal) => apiClient.get('/api/tools', { signal }),
 *   deps: [toolId],
 *   onSuccess: (data) => console.log('Success!', data),
 *   onError: (error) => console.error('Error:', error),
 * });
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorCard message={error} onRetry={retry} />;
 * return <div>{data.tools.length} tools found</div>;
 * ```
 */
export function useApiCall<T extends BaseResponse>(
  options: UseApiCallOptions<T>
): UseApiCallResult<T> {
  const { apiFn, deps = [], immediate = true, onError, onSuccess } = options;
  const { isRefreshingToken } = useAuth();

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(immediate);

  // Track if component is mounted to avoid state updates after unmount
  const isMounted = useRef(true);

  // Track the actual API loading state (separate from global loading)
  const isApiLoading = useRef(false);

  // Track the current AbortController to cancel in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Track isRefreshingToken with a ref to avoid stale closure issues
  const isRefreshingTokenRef = useRef(isRefreshingToken);

  // Update ref when isRefreshingToken changes
  useEffect(() => {
    isRefreshingTokenRef.current = isRefreshingToken;
  }, [isRefreshingToken]);

  /**
   * Execute the API call
   */
  const execute = useCallback(async () => {
    // Abort any previous in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    isApiLoading.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFn(signal);

      // Don't check isMounted here - let the state updates happen
      // React will ignore them if component is unmounted

      if (result.ok) {
        setData(result.data as T);
        setError(null);
        try {
          onSuccess?.(result.data as T);
        } catch (callbackErr) {
          console.error('[useApiCall] onSuccess callback threw error:', callbackErr);
        }
      } else {
        // Don't set error if token refresh is in progress
        // The error will be set after refresh completes (see useEffect below)
        if (!isRefreshingTokenRef.current) {
          const errorMessage = result.message || 'errors.generic';
          setError(errorMessage);
          setData(null);
          try {
            onError?.(errorMessage);
          } catch (callbackErr) {
            console.error('[useApiCall] onError callback threw error:', callbackErr);
          }
        }
      }
    } catch (err) {
      // Silently swallow AbortError — no state update, no callback
      if ((err instanceof Error || err instanceof DOMException) && err.name === 'AbortError')
        return;

      // Don't check isMounted here either

      // Don't set error if token refresh is in progress
      if (!isRefreshingTokenRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'errors.unknown';
        setError(errorMessage);
        setData(null);
        try {
          onError?.(errorMessage);
        } catch (callbackErr) {
          console.error('[useApiCall] onError callback threw error:', callbackErr);
        }
      }
    } finally {
      // Always execute finally block, React will ignore setState if unmounted
      isApiLoading.current = false;
      // Only set loading to false if token refresh is not in progress
      if (!isRefreshingTokenRef.current) {
        setIsLoading(false);
      }
    }
  }, [apiFn, onError, onSuccess]); // Removed isRefreshingToken from dependencies

  /**
   * Retry function - clears error and re-executes
   */
  const retry = useCallback(() => {
    setError(null);
    execute();
  }, [execute]);

  /**
   * Execute on mount if immediate is true, and when deps change.
   *
   * Deferred via Promise.resolve() so React StrictMode's synchronous cleanup
   * can set the `aborted` flag before the microtask fires. In StrictMode the
   * first mount's microtask is cancelled and only the second mount's request
   * actually starts, preventing a spurious aborted request in the proxy log.
   */
  useEffect(() => {
    if (!immediate) return;

    let aborted = false;

    void Promise.resolve().then(() => {
      if (!aborted) execute();
    });

    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  /**
   * Handle token refresh state changes
   * - Keep loading state true during token refresh
   * - Clear errors during token refresh
   */
  useEffect(() => {
    if (isRefreshingToken) {
      // Token refresh started - show loading, hide errors
      setIsLoading(true);
      setError(null);
    } else {
      // Token refresh completed - update loading state based on actual API state
      if (!isApiLoading.current) {
        setIsLoading(false);
      }
    }
  }, [isRefreshingToken]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMounted.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    data,
    error,
    isLoading,
    retry,
    execute,
  };
}
